import { Button, Divider, Form, Row, Spin } from "antd";
import {
  downloadJsonReportZip,
  getJsonReportSummary,
  JsonReportDetail,
  JsonReportView,
} from "api/generated";
import { FormItem } from "common/components/form/FormItem";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import {
  handleAxiosError,
  handleZipDownload,
} from "common/helpers/generalUtils";
import { useDateRange } from "context/DateRangeContext";
import { useClient } from "hooks/useClient";
import { useEffect, useMemo, useState } from "react";
import JsonReportsTable from "routes/jsonReport/components/JsonReportTable";
import { DownloadOutlined } from "@ant-design/icons";
import { DownloadJsonsDrawer } from "./components/DownloadJsonsDrawer";
import { TransferItem } from "./components/ReportSelectorPanel";
import { hasNoErrors } from "routes/jsonReport/components/JsonReportUtils";

function DownloadJsonReportsScreen() {
  const { client } = useClient();
  const [isLoading, setIsLoading] = useState(false);
  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { endDate } = getFormattedDates();

  const [summary, setSummary] = useState<JsonReportDetail[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Obtiene la lista de Reportes Json para el periodo elegido ─────────────
  useEffect(() => {
    setIsLoading(true);
    getJsonReportSummary({
      client,
      path: { reportType: "MENSUAL", date: endDate },
    })
      .then(({ data }) => setSummary(data ?? []))
      .catch((err) =>
        handleAxiosError(
          err,
          "No fue posible obtener la lista de Reportes Json",
        ),
      )
      .finally(() => setIsLoading(false));
  }, [client, endDate]);

  // ── Mapeo de summary hacia Items selecccionables para descargar ───────
  const transferDataSource: TransferItem[] = useMemo(
    () =>
      summary.map((detail, idx) => {
        const hasReport = !!detail.jsonReport?.reportName;
        const hasError = !hasNoErrors(detail.jsonReport?.errors);
        return {
          ...detail,
          key: detail.distributionPointId ?? `item-${idx}`,
          disabled: !hasReport || hasError,
          hasError,
          hasReport,
        };
      }),
    [summary],
  );

  // ── Mapeo de summary hacia listado de reportes disponibles ───────
  const jsonReports: JsonReportView[] = useMemo(
    () => summary.flatMap((x) => (x.jsonReport ? [x.jsonReport] : [])),
    [summary],
  );

  const downloadableCount: number = useMemo(
    () => transferDataSource.filter((i) => !i.disabled).length,
    [transferDataSource],
  );

  // ── Download ─────────────────────────────────────────────────────
  function handleConfirmDownload(ids: string[]) {
    setIsLoading(true);
    downloadJsonReportZip({
      client,
      body: {
        targetDate: endDate,
        reportType: "MENSUAL",
        distributionPointIds: ids.length > 0 ? ids : undefined,
      },
    })
      .then((data) => {
        handleZipDownload(data);
        setDrawerOpen(false);
      })
      .catch((err) => handleAxiosError(err, "No fue posible descargar Jsons"))
      .finally(() => setIsLoading(false));
  }

  return (
    <Spin spinning={isLoading}>
      <Form layout="vertical">
        <Row gutter={16} align="bottom">
          <ResponsiveCol size="lg">
            <FormItem label="Seleccionar el día a consultar">
              <DateRangePicker
                config={{
                  allowedTypes: [DateFilterType.MONTH],
                  defaultType: DateFilterType.MONTH,
                  showQuickFilters: true,
                }}
                rangeTimeValue={rangeTimeValue}
                setRangeTimeValue={setRangeTimeValue}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol size="lg">
            <FormItem>
              <Button
                style={{ width: "100%" }}
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => setDrawerOpen(true)}
                disabled={jsonReports.length === 0}
              >
                Descargar Reportes Mensales en ZIP ({downloadableCount}{" "}
                disponibles)
              </Button>
            </FormItem>
          </ResponsiveCol>
        </Row>

        <ResponsiveCol size="xl">
          <Divider orientation="left" style={{ borderColor: "#7cb305" }}>
            Reportes Json Mensuales Existentes
          </Divider>
          <JsonReportsTable
            data={jsonReports}
            loading={isLoading}
            mode="readonly"
            fixedColumns={["distributionName"]}
          />
        </ResponsiveCol>
      </Form>

      <DownloadJsonsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onConfirm={handleConfirmDownload}
        isLoading={isLoading}
        dataSource={transferDataSource}
      />
    </Spin>
  );
}

export default DownloadJsonReportsScreen;
